import { useState } from "react";
import { LuCircleCheck, LuFileText, LuUpload } from "react-icons/lu";
import { useController, useFormContext } from "react-hook-form";

import useUploadHook from "components/uploadHook";

import { Field, FieldError } from "./fields";
import { COVER_LETTER_MAX } from "./steps";

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
};

// The browser's type when it has one, else by extension ("CV.PDF" too).
const contentTypeOf = (file) => {
  if (file.type) return file.type;
  const ext = `.${file.name.split(".").pop().toLowerCase()}`;
  return Object.keys(ACCEPT).find((type) => ACCEPT[type].includes(ext));
};

const EXTENSIONS = Object.values(ACCEPT).flat();
const ACCEPT_ATTR = [...EXTENSIONS, ...Object.keys(ACCEPT)].join(",");

const fileProblem = (file) => {
  const ext = `.${file.name.split(".").pop().toLowerCase()}`;
  if (!EXTENSIONS.includes(ext)) return "Upload a PDF or Word document.";
  if (file.size > MAX_BYTES) return "That file is over 5 MB. Try a smaller PDF.";
  if (file.size === 0) return "That file is empty. Choose another one.";
  return null;
};

// A plain <label> around the file input: the browser opens its own picker
// once, which works the same on iPhone, Android and desktop. Drag and drop
// is an extra on desktop.
function FilePicker({ busy, invalid, describedBy, onFile, children, className }) {
  const [dragging, setDragging] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && !busy) onFile(file);
  };
  return (
    <label
      className={className}
      data-active={dragging}
      data-invalid={invalid}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <input
        type="file"
        accept={ACCEPT_ATTR}
        disabled={busy}
        aria-describedby={describedBy}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onFile(file);
        }}
      />
      {children}
    </label>
  );
}

function UploadedFile({ name, onFile, busy }) {
  return (
    <div className="cr-file">
      <span className="cr-file-icon" aria-hidden="true">
        <LuFileText />
      </span>
      <div className="cr-file-body">
        <p className="cr-file-name">{name || "Your CV"}</p>
        <p className="cr-file-status">
          {busy ? <span className="cr-spinner" aria-hidden="true" /> : <LuCircleCheck aria-hidden="true" />}
          {busy ? "Uploading the new file…" : "Uploaded"}
        </p>
      </div>
      <FilePicker busy={busy} onFile={onFile} className="cr-btn cr-btn--secondary cr-btn--sm cr-file-replace">
        Replace
      </FilePicker>
    </div>
  );
}

function CvDrop() {
  const { setValue, watch } = useFormContext();
  const {
    field: { value: resume, onChange: setResume },
    fieldState: { error: resumeError },
  } = useController({ name: "resume", rules: { required: "Add your CV to continue" } });
  const { uploadLoading, handleFile } = useUploadHook();
  const [uploadError, setUploadError] = useState("");
  const resumeName = watch("resume_name");

  const upload = async (file) => {
    const problem = fileProblem(file);
    setUploadError(problem || "");
    if (problem) return;
    const url = await handleFile({ target: { files: [file] } }, { contentType: contentTypeOf(file) });
    if (typeof url !== "string") {
      setUploadError("The upload didn't finish. Check your connection and try again.");
      return;
    }
    setValue("resume_name", file.name, { shouldDirty: true });
    setResume(url);
  };

  const error = uploadError || resumeError?.message;
  const errorId = error ? "cr-cv-error" : undefined;

  return (
    <div className="cr-field cr-span-2">
      <p className="cr-label">CV</p>
      {resume ? (
        <UploadedFile name={resumeName} onFile={upload} busy={uploadLoading} />
      ) : (
        <FilePicker
          className="cr-drop"
          busy={uploadLoading}
          invalid={Boolean(error)}
          describedBy={errorId}
          onFile={upload}
        >
          <span className="cr-drop-icon" aria-hidden="true">
            {uploadLoading ? <span className="cr-spinner" /> : <LuUpload />}
          </span>
          <span className="cr-drop-title">
            {uploadLoading ? "Uploading…" : <><b>Choose a file</b> or drag it here</>}
          </span>
          <span className="cr-hint">PDF or Word, up to 5 MB</span>
        </FilePicker>
      )}
      <FieldError id={errorId} message={error} />
    </div>
  );
}

function CoverLetter() {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext();
  const length = (watch("cover_letter") || "").length;
  return (
    <Field
      label="Anything you'd like us to know?"
      optional
      hint="Why this role, your availability, or the branch you'd like to work at."
      error={errors.cover_letter?.message}
      className="cr-span-2"
    >
      {({ id, describedBy, invalid }) => (
        <>
          <textarea
            id={id}
            className="cr-textarea"
            rows={5}
            maxLength={COVER_LETTER_MAX}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            {...register("cover_letter")}
          />
          <span className="cr-counter" aria-hidden="true">
            {length} / {COVER_LETTER_MAX}
          </span>
        </>
      )}
    </Field>
  );
}

export default function CvStep() {
  return (
    <div className="cr-fields">
      <CvDrop />
      <CoverLetter />
    </div>
  );
}
